import { memo, useMemo } from "react";
import { styled, type Theme } from "@mui/material/styles";
import {
  tokenizePubmedQuery,
  type PubmedQueryToken,
  type PubmedQueryTokenKind,
} from "../../../../lib/pubmed_query_tokens";

const QueryCode = styled("code")({
  fontFamily: "monospace",
  fontSize: "inherit",
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
});

/** What colours each kind of token; whitespace and plain terms keep the surrounding text colour. */
function colourOf(theme: Theme, kind: PubmedQueryTokenKind): string | undefined {
  switch (kind) {
    case "operator":
      return theme.palette.primary.main;
    case "fieldTag":
      return theme.palette.info.main;
    case "phrase":
      return theme.palette.warning.dark;
    case "parenthesis":
    case "range":
      return theme.palette.text.secondary;
    case "whitespace":
    case "term":
      return undefined;
  }
}

const QueryToken = styled("span")<{ $kind: PubmedQueryTokenKind; $unmatched: boolean }>(
  ({ theme, $kind, $unmatched }) => ({
    color: $unmatched ? theme.palette.error.main : colourOf(theme, $kind),
    fontWeight: $kind === "operator" || $kind === "parenthesis" ? 700 : undefined,
    textDecoration: $unmatched ? "underline wavy" : undefined,
    textDecorationColor: $unmatched ? theme.palette.error.main : undefined,
  })
);

/** Why a token is flagged, for its tooltip. */
function unmatchedReason(token: PubmedQueryToken): string {
  switch (token.kind) {
    case "phrase":
      return "This quotation mark is never closed";
    case "fieldTag":
      return 'This "[" is never closed';
    case "parenthesis":
      return token.text === "(" ? 'This "(" is never closed' : 'This ")" has no matching "("';
    default:
      return 'This "]" has no matching "["';
  }
}

export interface PubmedQueryTextProps {
  /** An Entrez query, exactly as typed. */
  query: string;
}

/** An Entrez (PubMed / PMC) query in monospace, syntax-coloured: operators, field tags, quoted
 *  phrases and parentheses each stand out, and anything left unclosed is underlined in red. The
 *  text is the query unchanged, so selecting and copying it gives back what was written. */
export const PubmedQueryText = memo(function PubmedQueryText({ query }: PubmedQueryTextProps) {
  const tokens = useMemo(() => tokenizePubmedQuery(query), [query]);

  return (
    <QueryCode>
      {tokens.map((token, index) => (
        <QueryToken
          key={index}
          $kind={token.kind}
          $unmatched={token.unmatched === true}
          title={token.unmatched ? unmatchedReason(token) : undefined}
        >
          {token.text}
        </QueryToken>
      ))}
    </QueryCode>
  );
});
